import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import {
  RouteComponentProps,
  withRouter
} from 'react-router-dom'
import {
  Container,
  Grid,
  Segment
} from 'semantic-ui-react'
// Local
import {
  API_URL,
  CONDITION,
  DISPLAY_MODE,
  ENCOUNTER,
  PAGES
} from '../../../constant'
import { api } from '../../../action/api'
import NavHeader from '../common/navHeader'
import EncounterDateWarning from '../common/encounterDateWarning'
import ClinicalAdminEncounterLeft from './clinicalAdminEncounterLeft'
import ClinicalAdminEncounterConditions from './clinicalAdminEncounterConditions'
import {
  Encounter,
  EncounterConditionDisplay,
  EncounterDetail
} from '../../authenticated/types'
import { ObjectValues } from '../../../types/types'
import If from '../../common/if'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

type ClinicalAdminEncounterOwnProps = {
  mode?: string | null
  encounterId: string
  onEncounterNav: (id: string) => void
  conditionGroups?: string[]
}

type ClinicalAdminEncounterProps = ClinicalAdminEncounterOwnProps & RouteComponentProps


type APIResponse = {
  data: EncounterDetail
}

/**
 * Provides the top-level Clinical Admin encounter detail component
 *
 * @param mode View mode. Options: 'smart', null. Default: null
 * @param encounterId Encounter ID
 * @param onEncounterNav Encounter navigation callback. Use this callback to
 *        navigate to another encounter. Argument: encounter ID
 */
function ClinicalAdminEncounter(props: ClinicalAdminEncounterProps): JSX.Element | null {
  const {
    mode: modeProp,
    encounterId: encounterIdProp,
    onEncounterNav,
    conditionGroups,
    history
  } = props

  const [ , setEncounterId ] = useState<string>(encounterIdProp)
  const [ encounterDetail, setEncounterDetail ] = useState<EncounterDetail | null>(null)
  const [ activeConditionId, setActiveConditionId ] = useState<string | null>(null)

  const fetchEncounter = (id: string) => {
    const url = `${API_URL.GET_ENCOUNTER_DETAIL_BY_ID}${id}`
    api.get(url).then(({ data }: APIResponse) => {
      setEncounterId(id)
      setEncounterDetail(data)
    })
  }

  useEffect(() => {
    fetchEncounter(encounterIdProp)
  }, [ encounterIdProp ])

  const spliceCondition = (
    condition: EncounterConditionDisplay,
    conditions: EncounterConditionDisplay[]
  ): EncounterConditionDisplay[] => conditions.map(c => {
    if (c.id === condition.id) {
      return condition
    }
    return c
  })

  const updateConditionCode = (
    condition: EncounterConditionDisplay,
    newCode: string
  ) => {
    const url = `${API_URL.UPDATE_CONDITION}/${condition.id}`
    const updatedCondition = {
      ...condition,
      conditionCode: newCode
    }
    api.put(url, updatedCondition).then(({ data }: { data: EncounterConditionDisplay }) => {
      if (encounterDetail && encounterDetail.conditions) {
        const updatedConditions = spliceCondition(
          data,
          encounterDetail.conditions
        )
        setEncounterDetail({
          ...encounterDetail,
          conditions: updatedConditions
        })
        setActiveConditionId(condition.id)
      }
    })
  }

  /**
   * @param condition Can be: null, condition or ID. An ID can be a condition or
   * a group identifier
   */
  const handleConditionActivate = (
    condition: EncounterConditionDisplay | string | null
  ) => {
    if (condition === null) {
      setActiveConditionId(null)
    } else {
      const id = typeof condition === 'string'
        ? condition
        : condition.id
      setActiveConditionId(id)
    }
  }

  /**
   * Currently clinical admin conditions are modal: either 1 condition
   * (or condition group) is active or no conditions are active
   */
  const handleConditionDeactivate = () => {
    setActiveConditionId(null)
  }

  const handleConditionRead = (condition: EncounterConditionDisplay | null) => {
    if (condition) {
      api.post(
        API_URL.AUDIT('READ', 'ENCOUNTER_CONDITION', condition.id),
        {}
      )
    }
  }

  const handleHeaderAction = (action: ObjectValues<typeof ENCOUNTER.ACTION>) => {
    if (!encounterDetail) {
      return
    }

    const {links} = encounterDetail
    if (action === ENCOUNTER.ACTION.PREVIOUS_ENCOUNTER) {
      if (links && links.prev) {
        onEncounterNav(links.prev)
      }
    }
    if (action === ENCOUNTER.ACTION.NEXT_ENCOUNTER) {
      if (links && links.next) {
        onEncounterNav(links.next)
      }
    }
    if (action === ENCOUNTER.ACTION.WORKLIST) {
      history.push(PAGES.HOME)
    }
  }

  /**
   * Top level router for clinical admin encounter condition actions
   * @param action The thing that happened
   * @param condition This can be a single condition, array of conditions,
   * a condition ID or NULL. Look to the action handler function to see what
   * it expects
   */
  const handleConditionAction = (
    action: ObjectValues<typeof CONDITION.ACTION>,
    condition: EncounterConditionDisplay | string | null
  ) => {
    if (action === CONDITION.ACTION.ACTIVATE) {
      handleConditionActivate(condition)
    }
    if (action === CONDITION.ACTION.DEACTIVATE) {
      handleConditionDeactivate()
    }
    if (action === CONDITION.ACTION.MARK_DISMISSED) {
      if (condition && typeof condition !== 'string') {
        updateConditionCode(condition, CONDITION.CODE.DISMISSED)
      }
    }
    if (action === CONDITION.ACTION.MARK_OPEN) {
      if (condition && typeof condition !== 'string') {
        updateConditionCode(condition, CONDITION.CODE.OPEN)
      }
    }
    if (action === CONDITION.ACTION.READ) {
      if (condition && typeof condition !== 'string') {
        handleConditionRead(condition)
      } else {
        handleConditionRead(null)
      }
    }
  }

  const renderDetail = (detail: EncounterDetail) => {
    const {encounter} = detail
    const {links} = detail
    const key = (new Date()).getTime()

    if (modeProp === DISPLAY_MODE.EMBEDDED) {
      return (
        <div className="header-padding">
          <Container>
            <SafeSegment attached className="no-margin">
              <SafeGrid stackable>
                <Grid.Row>
                  <Grid.Column>
                    <ClinicalAdminEncounterConditions
                      key={ `encounter-conditions-${key}` }
                      mode={ modeProp }
                      encounterDetail={ detail }
                      conditionGroups={ conditionGroups }
                      activeConditionId={ activeConditionId }
                      onConditionAction={ handleConditionAction }
                    />
                  </Grid.Column>
                </Grid.Row>
              </SafeGrid>
            </SafeSegment>
          </Container>
        </div>
      )
    }

    return (
      <div className="header-padding">
        <Container>
          <If isTrue={ !!encounter && !!links }>
            <NavHeader
              mode={ modeProp }
              encounter={ encounter as Encounter }
              links={ links }
              onAction={ handleHeaderAction }
            />
          </If>
          <If isTrue={ !!encounter }>
            <EncounterDateWarning
              encounter={ encounter }
            />
          </If>
          <SafeSegment attached className="no-margin">
            <SafeGrid stackable>
              <Grid.Row>
                <Grid.Column width={ 4 }>
                  <ClinicalAdminEncounterLeft encounterDetail={ detail } />
                </Grid.Column>
                <Grid.Column width={ 12 }>
                  <ClinicalAdminEncounterConditions
                    key={ `encounter-conditions-${key}` }
                    mode={ modeProp }
                    encounterDetail={ detail }
                    conditionGroups={ conditionGroups }
                    activeConditionId={ activeConditionId }
                    onConditionAction={ handleConditionAction }
                  />
                </Grid.Column>
              </Grid.Row>
            </SafeGrid>
          </SafeSegment>
        </Container>
      </div>
    )
  }

  return encounterDetail
    ? renderDetail(encounterDetail)
    : null
}

export default withRouter(ClinicalAdminEncounter)
